/**
 * Iter 200a · ClientsInlineTable
 *
 * Feb 2026 R293zze · Expanded to a full-spreadsheet view with
 *  • Every client field inline-editable in-row
 *  • Row selection + a Bulk Edit toolbar that applies one field value
 *    to every selected row via parallel PATCH /api/v1/clients/{cid}
 *
 * Hit Enter/Tab to commit, Esc to cancel. Industry + Sub-industry
 * cells use SearchableSelect so a large catalog stays browsable.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Search as SearchIcon, ExternalLink, Check, X, Edit3, CheckSquare, Square } from "lucide-react";
import SearchableSelect from "@/components/ui/SearchableSelect";

const CURRENCY_OPTS = ["", "INR", "USD", "EUR", "GBP", "AUD", "CAD", "AED", "SGD"];
const SIZE_OPTS = ["", "1-10", "11-50", "51-200", "201-1000", "1000+"];
const TAX_LABEL_OPTS = ["", "GSTIN", "PAN", "VAT", "TIN", "EIN", "ABN", "Other"];
const CREDIT_OPTS = ["", "AAA", "AA", "A", "BBB", "BB", "B", "not_rated"];
const RISK_OPTS = ["", "low", "medium", "high", "churning"];

const COLS = [
  { key: "name",                   label: "Name *",             type: "text", w: 200 },
  { key: "client_code",            label: "Code",               type: "text", w: 110 },
  { key: "display_name",           label: "Display name",       type: "text", w: 180 },
  { key: "legal_name",             label: "Legal name",         type: "text", w: 200 },
  { key: "contact_email",          label: "Email",              type: "email", w: 200 },
  { key: "contact_phone",          label: "Phone",              type: "text", w: 140 },
  { key: "website",                label: "Website",            type: "text", w: 200 },
  { key: "industry",               label: "Industry",           type: "industry", w: 160 },
  { key: "sub_industry",           label: "Sub-industry",       type: "sub_industry", w: 180 },
  { key: "company_size",           label: "Size",               type: "select", options: SIZE_OPTS, w: 110 },
  { key: "billing_address_line1",  label: "Address line 1",     type: "text", w: 220 },
  { key: "billing_address_line2",  label: "Address line 2",     type: "text", w: 180 },
  { key: "billing_city",           label: "City",               type: "text", w: 130 },
  { key: "billing_state",          label: "State",              type: "text", w: 120 },
  { key: "billing_country",        label: "Country",            type: "text", w: 130 },
  { key: "billing_postal_code",    label: "Postal code",        type: "text", w: 110 },
  { key: "tax_id_label",           label: "Tax ID label",       type: "select", options: TAX_LABEL_OPTS, w: 120 },
  { key: "tax_id_value",           label: "Tax ID",             type: "text", w: 160 },
  { key: "billing_email",          label: "Billing email",      type: "email", w: 200 },
  { key: "billing_currency",       label: "Currency",           type: "select", options: CURRENCY_OPTS, w: 110 },
  { key: "payment_terms_days",     label: "Net terms (days)",   type: "number", w: 120 },
  { key: "credit_rating",          label: "Credit rating",      type: "select", options: CREDIT_OPTS, w: 130 },
  { key: "csat_score",             label: "CSAT (1-5)",         type: "number", w: 100 },
  { key: "retention_risk",         label: "Retention risk",     type: "select", options: RISK_OPTS, w: 130 },
  { key: "renewal_date",           label: "Renewal date",       type: "date", w: 130 },
  { key: "notes",                  label: "Notes",              type: "text", w: 240 },
];

const BULK_FIELDS = COLS.filter((c) => !["name", "client_code"].includes(c.key));

export default function ClientsInlineTable({ rows, onSaved, industries, subIndustriesMap }) {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // {id, key, value}
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => COLS.some((c) => String(r[c.key] || "").toLowerCase().includes(t)));
  }, [rows, q]);

  const allOnPageSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allOnPageSelected) filtered.forEach((r) => next.delete(r.id));
    else filtered.forEach((r) => next.add(r.id));
    setSelected(next);
  };
  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const startEdit = (id, key, current) => setEditing({ id, key, value: current ?? "" });
  const cancel = () => setEditing(null);

  const commit = async () => {
    if (!editing) return;
    const { id, key, value } = editing;
    const row = rows.find((r) => r.id === id);
    if (!row) { cancel(); return; }
    const before = row[key] ?? "";
    if (String(before) === String(value)) { cancel(); return; }
    if (key === "name" && !String(value).trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const col = COLS.find((c) => c.key === key);
      let v = typeof value === "string" ? value.trim() : value;
      if (v === "") v = null;
      else if (col?.type === "number") v = Number(v);
      const patch = { [key]: v };
      if (key === "industry" && row.sub_industry) patch.sub_industry = null;
      await api.patch(`/clients/${id}`, patch);
      toast.success("Saved");
      cancel();
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Save failed"));
    } finally { setSaving(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };

  const applyBulk = async (fieldKey, value) => {
    const col = COLS.find((c) => c.key === fieldKey);
    let v = typeof value === "string" ? value.trim() : value;
    if (v === "") v = null;
    else if (col?.type === "number") v = Number(v);
    const ids = [...selected];
    setSaving(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => api.patch(`/clients/${id}`, { [fieldKey]: v })),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (fail === 0) toast.success(`Updated ${ok} client${ok === 1 ? "" : "s"}`);
      else toast.warning(`Updated ${ok} · ${fail} failed`);
      setBulkOpen(false);
      onSaved?.();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-2" data-testid="clients-inline-table">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <SearchIcon className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter across all fields…"
                  className="h-9 rounded-none pl-7 w-72 text-sm"
                  data-testid="clients-inline-search" />
        </div>
        {selected.size > 0 && (
          <>
            <Button size="sm" variant="outline" className="h-9 rounded-none text-xs"
                       onClick={() => setBulkOpen(true)}
                       data-testid="clients-bulk-edit-btn">
              <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Bulk edit {selected.size}
            </Button>
            <Button size="sm" variant="ghost" className="h-9 rounded-none text-xs"
                       onClick={() => setSelected(new Set())}
                       data-testid="clients-bulk-clear-btn">
              Clear selection
            </Button>
          </>
        )}
        <div className="text-[11px] text-muted-foreground ml-auto">
          {filtered.length} of {rows.length} · click any cell to edit · Enter to save · Esc to cancel
        </div>
      </div>
      <div className="border border-border overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 border-b border-border sticky top-0">
            <tr>
              <th className="w-9 px-2 py-1.5">
                <button type="button" onClick={toggleAll} data-testid="clients-select-all">
                  {allOnPageSelected
                    ? <CheckSquare className="w-3.5 h-3.5 text-primary" />
                    : <Square className="w-3.5 h-3.5 text-muted-foreground" />}
                </button>
              </th>
              {COLS.map((c) => (
                <th key={c.key} className="text-left px-2 py-1.5 uppercase tracking-widest font-semibold text-[10px]" style={{ minWidth: c.w }}>
                  {c.label}
                </th>
              ))}
              <th className="text-right px-2 py-1.5 w-10">→</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id}
                     className={`border-b border-border hover:bg-muted/20 ${selected.has(r.id) ? "bg-primary/5" : ""}`}
                     data-testid={`client-row-${r.id}`}>
                <td className="px-2 py-0.5 align-middle">
                  <button type="button" onClick={() => toggleRow(r.id)}
                            data-testid={`client-select-${r.id}`}>
                    {selected.has(r.id)
                      ? <CheckSquare className="w-3.5 h-3.5 text-primary" />
                      : <Square className="w-3.5 h-3.5 text-muted-foreground" />}
                  </button>
                </td>
                {COLS.map((c) => {
                  const isEditing = editing?.id === r.id && editing?.key === c.key;
                  const val = r[c.key];
                  return (
                    <td key={c.key} className="px-1 py-0.5 align-middle" style={{ minWidth: c.w }}>
                      {isEditing ? (
                        <div className="flex items-center gap-0.5">
                          {c.type === "industry" ? (
                            <SearchableSelect
                              value={editing.value}
                              onValueChange={(v) => setEditing((e) => ({ ...e, value: v }))}
                              options={industries}
                              placeholder="—"
                              clearable
                              width="100%"
                              data-testid={`cell-select-industry-${r.id}`}
                            />
                          ) : c.type === "sub_industry" ? (
                            <SearchableSelect
                              value={editing.value}
                              onValueChange={(v) => setEditing((e) => ({ ...e, value: v }))}
                              options={subIndustriesMap[r.industry] || []}
                              placeholder={r.industry ? "—" : "Set industry first"}
                              clearable
                              disabled={!r.industry}
                              width="100%"
                              data-testid={`cell-select-sub-${r.id}`}
                            />
                          ) : c.type === "select" ? (
                            <select
                              autoFocus
                              value={editing.value || ""}
                              onChange={(e) => setEditing((s) => ({ ...s, value: e.target.value }))}
                              onBlur={commit}
                              onKeyDown={onKey}
                              className="h-8 rounded-none text-xs border border-border bg-background w-full"
                              data-testid={`cell-select-${c.key}-${r.id}`}
                            >
                              {(c.options || []).map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                            </select>
                          ) : (
                            <Input
                              autoFocus
                              value={editing.value}
                              type={c.type === "email" ? "email"
                                    : c.type === "number" ? "number"
                                    : c.type === "date" ? "date" : "text"}
                              onChange={(e) => setEditing((s) => ({ ...s, value: e.target.value }))}
                              onKeyDown={onKey}
                              onBlur={commit}
                              className="h-8 rounded-none text-xs"
                              data-testid={`cell-input-${c.key}-${r.id}`}
                            />
                          )}
                          {(c.type === "industry" || c.type === "sub_industry") && (
                            <>
                              <button className="w-6 h-8 border border-border hover:bg-emerald-100" onClick={commit} disabled={saving}
                                          data-testid={`cell-save-${c.key}-${r.id}`}>
                                <Check className="w-3 h-3 mx-auto text-emerald-700" />
                              </button>
                              <button className="w-6 h-8 border border-border hover:bg-rose-100" onClick={cancel}>
                                <X className="w-3 h-3 mx-auto text-rose-700" />
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(r.id, c.key, val)}
                          className={`w-full text-left px-1.5 py-1 truncate ${val != null && val !== "" ? "" : "text-muted-foreground italic"}`}
                          title={val || "click to edit"}
                          data-testid={`cell-${c.key}-${r.id}`}
                        >
                          {val != null && val !== ""
                            ? String(val)
                            : (c.key === "sub_industry" && !r.industry ? "—" : "click to edit")}
                        </button>
                      )}
                    </td>
                  );
                })}
                <td className="text-right px-1">
                  <Link to={`/app/clients/${r.id}`} className="inline-block p-1 text-muted-foreground hover:text-primary"
                          data-testid={`client-open-${r.id}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={COLS.length + 2} className="text-center text-xs text-muted-foreground italic p-6">
                No clients match &quot;{q}&quot;
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <BulkEditDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        count={selected.size}
        fields={BULK_FIELDS}
        industries={industries}
        subIndustriesMap={subIndustriesMap}
        onApply={applyBulk}
        saving={saving}
      />
    </div>
  );
}

function BulkEditDialog({ open, onOpenChange, count, fields, industries, subIndustriesMap, onApply, saving }) {
  const [fieldKey, setFieldKey] = useState("");
  const [value, setValue] = useState("");
  const col = fields.find((c) => c.key === fieldKey);

  const reset = () => { setFieldKey(""); setValue(""); };
  const handle = async () => {
    if (!fieldKey) { toast.error("Pick a field to update"); return; }
    await onApply(fieldKey, value);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="rounded-none max-w-md" data-testid="clients-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Bulk edit {count} client{count === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Pick a field and set the value — it will be applied to every selected client. Leave blank to clear the field.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest font-semibold">Field</Label>
            <Select value={fieldKey} onValueChange={(v) => { setFieldKey(v); setValue(""); }}>
              <SelectTrigger className="rounded-none h-9 mt-1" data-testid="bulk-field-select">
                <SelectValue placeholder="Choose a field…" />
              </SelectTrigger>
              <SelectContent>
                {fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {col && (
            <div>
              <Label className="text-xs uppercase tracking-widest font-semibold">New value</Label>
              {col.type === "industry" ? (
                <SearchableSelect
                  value={value}
                  onValueChange={setValue}
                  options={industries}
                  placeholder="—"
                  clearable
                  width="100%"
                  data-testid="bulk-value-industry"
                />
              ) : col.type === "sub_industry" ? (
                <SearchableSelect
                  value={value}
                  onValueChange={setValue}
                  options={Object.values(subIndustriesMap || {}).flat()}
                  placeholder="—"
                  clearable
                  width="100%"
                  data-testid="bulk-value-sub"
                />
              ) : col.type === "select" ? (
                <select
                  value={value || ""}
                  onChange={(e) => setValue(e.target.value)}
                  className="h-9 rounded-none text-sm border border-border bg-background w-full mt-1 px-2"
                  data-testid={`bulk-value-${col.key}`}
                >
                  {(col.options || []).map((o) => <option key={o} value={o}>{o || "— (clear)"}</option>)}
                </select>
              ) : (
                <Input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  type={col.type === "number" ? "number"
                        : col.type === "date" ? "date"
                        : col.type === "email" ? "email" : "text"}
                  className="h-9 rounded-none text-sm mt-1"
                  data-testid={`bulk-value-${col.key}`}
                />
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none"
                     data-testid="bulk-cancel-btn">Cancel</Button>
          <Button onClick={handle} disabled={saving || !fieldKey} className="rounded-none"
                     data-testid="bulk-apply-btn">
            {saving ? "Applying…" : `Apply to ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
